/**
 * Renderiza a lista de elementos devolvida pela API (contrato §5.1): blocos de gráficos
 * (com título de bloco opcional, 2 por linha como no legado), tabelas, avisos, títulos,
 * legendas, notas explicativas (o `st.info` do legado) e colunas lado a lado.
 */
import { Alert, Grid, List, SimpleGrid, Stack, Text, Title } from '@mantine/core';

import { Grafico, type GraficoApi } from './Grafico';
import { TabelaDados, type TabelaApi } from './TabelaDados';

export interface BlocoApi {
  bloco: string | null;
  graficos: GraficoApi[];
  /** Gráficos por linha em telas largas (padrão 2, como o legado). */
  colunas?: number;
}

export type ElementoApi =
  | { tipo: 'blocos'; blocos: BlocoApi[] }
  | { tipo: 'tabela'; tabela: TabelaApi }
  | { tipo: 'aviso'; texto: string }
  | { tipo: 'titulo'; texto: string }
  | { tipo: 'legenda'; texto: string }
  | { tipo: 'nota'; itens: { termo: string; texto: string }[] }
  /** Colunas lado a lado; `larguras` são proporções (padrão: iguais). Empilham no celular. */
  | { tipo: 'colunas'; colunas: ElementoApi[][]; larguras?: number[] };

function Nota({ itens }: { itens: { termo: string; texto: string }[] }) {
  return (
    <Alert color="blue" variant="light">
      <List size="sm" spacing={4}>
        {itens.map((item, i) => (
          <List.Item key={i}>
            {item.termo && <b>{item.termo}: </b>}
            {item.texto}
          </List.Item>
        ))}
      </List>
    </Alert>
  );
}

function Colunas({ colunas, larguras }: { colunas: ElementoApi[][]; larguras?: number[] }) {
  const pesos = larguras ?? colunas.map(() => 1);
  const total = pesos.reduce((a, b) => a + b, 0);
  return (
    <Grid gap="lg">
      {colunas.map((elementos, i) => (
        <Grid.Col key={i} span={{ base: 12, lg: Math.round((12 * (pesos[i] ?? 1)) / total) }}>
          <Elementos elementos={elementos} />
        </Grid.Col>
      ))}
    </Grid>
  );
}

export function Elementos({ elementos }: { elementos: ElementoApi[] }) {
  return (
    <Stack gap="lg">
      {elementos.map((el, i) => {
        if (el.tipo === 'aviso') {
          return (
            <Alert key={i} color="yellow">
              {el.texto}
            </Alert>
          );
        }
        if (el.tipo === 'titulo') {
          return (
            <Title key={i} order={4}>
              {el.texto}
            </Title>
          );
        }
        if (el.tipo === 'legenda') {
          return (
            <Text key={i} size="xs" c="dimmed">
              {el.texto}
            </Text>
          );
        }
        if (el.tipo === 'nota') return <Nota key={i} itens={el.itens} />;
        if (el.tipo === 'colunas')
          return <Colunas key={i} colunas={el.colunas} larguras={el.larguras} />;
        if (el.tipo === 'tabela') {
          return <TabelaDados key={i} tabela={el.tabela} alturaAutomatica />;
        }
        return el.blocos.map((bloco, j) => (
          <Stack key={`${i}-${j}`} gap="xs">
            {bloco.bloco && <Title order={4}>{bloco.bloco}</Title>}
            <SimpleGrid cols={{ base: 1, lg: bloco.colunas ?? 2 }}>
              {bloco.graficos.map((g) => (
                <Grafico key={g.id} grafico={g} />
              ))}
            </SimpleGrid>
          </Stack>
        ));
      })}
    </Stack>
  );
}
